import { isAfter, parseISO } from 'date-fns';
import { isString } from 'lodash';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';

export const pascalCase = (str: string): string =>
    str
        .replace(/[^a-z0-9]+/gi, ' ')
        .replace(/(\w)(\w*)/g, (g0, g1, g2) => `${g1.toUpperCase()}${g2.toLowerCase()}`)
        .replace(/\s+/gi, '');

export const isMessage = (message: IntlMessage) =>
    isObject(message) && typeof message.id !== 'undefined';

export const addNonBreakingSpaces = (str) => str.replace(/&nbsp;/g, '\u00a0');

export const cleanExternalLinks = (str) =>
    str !== null
        ? str.replace(/(<a\\b[^<>]*href=['"]?http[^<>]+)>/gi, '$1 target="_blank">')
        : null;

export const getComponentFromName = (
    components: Record<string, React.ComponentType>,
    name,
    defaultComponentName = null,
): React.ComponentType | null => {
    const defaultComponent =
        (isString(defaultComponentName)
            ? components[pascalCase(defaultComponentName)]
            : defaultComponentName) || null;
    if (name === null) {
        return defaultComponent;
    }
    if (typeof name !== 'string') {
        return name;
    }
    const componentName = pascalCase(name);
    return components[componentName] || defaultComponent;
};

export const formatPhoneInput = (value) => {
    const str = `${value}`.replace(/\D/g, '');
    const { length } = str;

    const startWithOne = str.replace(/\D/g, '').match(/^1/) !== null;
    if ((!startWithOne && length < 4) || (startWithOne && length <= 4)) {
        return str;
    }
    if ((!startWithOne && length < 7) || (startWithOne && length <= 7)) {
        return str
            .replace(/\D/g, '')
            .replace(/^(1|)?(\d{3})(\d+)?$/, '$1 ($2) $3')
            .replace(/^([\s+]+)?(.*?)([\s-]+)?$/, '$2');
    }

    return str
        .replace(/\D/g, '')
        .replace(/^(1|)?(\d{3})(\d{3})(\d+)$/, '$1 ($2) $3-$4')
        .replace(/^([\s+]+)?(.*?)([\s-]+)?$/, '$2');
};

export const getImageAtSize = (image, size, webp = false) => {
    if (image === null) {
        return null;
    }
    const { sizes: imagesSizes = [], ...originalImage } = image;
    return (
        [...(isArray(size) ? size : [size]), 'original']
            .reduce(
                (acc, currentSize) =>
                    webp ? [...acc, `${currentSize}_webp`, currentSize] : [...acc, currentSize],
                [],
            )
            .reduce(
                (selectedImage, currentSize) =>
                    selectedImage ||
                    (imagesSizes || []).find(({ id }) => id === currentSize) ||
                    null,
                null,
            ) || originalImage
    );
};

export const isIos = () =>
    ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(
        navigator.platform,
    ) ||
    // iPad on iOS 13 detection
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document);

export function getNextQuestionIndex(
    questions: Question[],
    answers: ParticipationAnswer[],
): number {
    const unansweredQuestions = questions.filter(
        ({ id }) =>
            answers.findIndex(({ question, valid = null }) => question === id && valid !== null) ===
            -1,
    );
    if (unansweredQuestions.length > 0) {
        const { id: unansweredId } = unansweredQuestions[0];
        return questions.findIndex(({ id }) => id === unansweredId);
    }

    if (unansweredQuestions.length === 0) {
        return questions.length - 1;
    }
    return 0;
}

export function getPageKey(it): string {
    const { id = null, type = null, game = null } = it || {};
    if (game !== null) {
        return `game-${game.id}`;
    }
    return type === 'page' || type === 'display' ? id : type;
}

export function isNewer(check, base): boolean {
    const { updatedAt: a = null } = check || {};
    const { updatedAt: b = null } = base || {};
    return (
        (b === null && a !== null) ||
        (a !== null && b !== null && isAfter(parseISO(a), parseISO(b)))
    );
}

export function mergeGame(oldGame = null, newGame = null) {
    if (oldGame === null) {
        return newGame;
    }
    if (newGame === null) {
        return oldGame;
    }

    const newGameIsFresh = isNewer(newGame, oldGame);
    const past = newGameIsFresh ? oldGame : newGame;
    const future = newGameIsFresh ? newGame : oldGame;

    const {
        questions: oldQuestions = [],
        questionsGroups: oldQuestionsGroups = [],
        ...pastProps
    } = past || {};
    const {
        questions: newQuestions = [],
        questionsGroups: newQuestionsGroups = [],
        ...futureProps
    } = future || {};

    const questions = (newQuestions || []).map((newQuestion) => {
        const { id: questionId = null } = newQuestion || {};
        const oldQuestion = (oldQuestions || []).find(({ id }) => id === questionId) || null;
        return { ...oldQuestion, ...newQuestion };
    });

    const questionsGroups = (newQuestionsGroups || []).map((newQuestionGroup) => {
        const { id: questionGroupId = null } = newQuestionGroup || {};
        const oldQuestionGroup =
            (oldQuestionsGroups || []).find(({ id }) => id === questionGroupId) || null;

        const { questions: oldQuestionsInGroup = [], ...oldQuestionGroupProps } = oldQuestionGroup;
        const { questions: newQuestionsInGroup = [], ...newQuestionGroupProps } = newQuestionGroup;

        const questionsInGroup = (newQuestionsInGroup || []).map((newQuestion) => {
            const { id: questionId = null } = newQuestion || {};
            const oldQuestion =
                (oldQuestionsInGroup || []).find(({ id }) => id === questionId) || null;
            return { ...oldQuestion, ...newQuestion };
        });

        return { ...oldQuestionGroupProps, ...newQuestionGroupProps, questions: questionsInGroup };
    });

    return {
        ...pastProps,
        ...futureProps,
        questions,
        questionsGroups,
    };
}
